/**
 * Canonical service order status definitions — single source of truth.
 *
 * DB stores:   'pending' | 'in-progress' | 'completed' | 'overdue' | 'cancelled'
 * API returns: same canonical values
 * Frontend:    uses these types + display labels + badge variants
 */

export type ServiceOrderStatus = 'pending' | 'in-progress' | 'completed' | 'overdue' | 'cancelled';

export type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

/**
 * Normalizes any raw status string (from DB, API, or legacy code) to canonical form.
 */
export function normalizeServiceOrderStatus(raw: string | null | undefined): ServiceOrderStatus {
  if (raw === null || raw === undefined || raw === '') {
    return 'pending';
  }
  const s = raw.toLowerCase().replace(/\s+/g, '-');
  if (s === 'active' || s === 'in-progress' || s === 'inprogress') {
    return 'in-progress';
  }
  if (s === 'complete' || s === 'completed') {
    return 'completed';
  }
  if (s === 'cancelled' || s === 'canceled') {
    return 'cancelled';
  }
  if (s === 'overdue') {
    return 'overdue';
  }
  return 'pending';
}

export const SERVICE_ORDER_STATUS_LABEL: Record<ServiceOrderStatus, string> = {
  pending: 'Pending',
  'in-progress': 'Active',
  completed: 'Complete',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

export const SERVICE_ORDER_STATUS_VARIANT: Record<ServiceOrderStatus, BadgeVariant> = {
  pending: 'warning',
  'in-progress': 'success',
  completed: 'info',
  overdue: 'danger',
  cancelled: 'danger',
};

/** Filter dropdown options (includes "All") */
export const SERVICE_ORDER_FILTER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'All', label: 'All Statuses' },
  { value: 'in-progress', label: 'Active' },
  { value: 'completed', label: 'Complete' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** Edit form dropdown options (excludes "overdue" — that's computed, not user-set) */
export const SERVICE_ORDER_EDIT_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'Active' },
  { value: 'completed', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
];

/* ── Service Request Status (portal feature) ── */

export type ServiceRequestStatus = 'pending' | 'approved' | 'converted' | 'rejected';

export function normalizeServiceRequestStatus(
  raw: string | null | undefined,
): ServiceRequestStatus {
  if (raw === null || raw === undefined || raw === '') {
    return 'pending';
  }
  const s = raw.toLowerCase();
  if (s === 'approved') {
    return 'approved';
  }
  if (s === 'converted') {
    return 'converted';
  }
  if (s === 'rejected') {
    return 'rejected';
  }
  return 'pending';
}

export const SERVICE_REQUEST_STATUS_LABEL: Record<ServiceRequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  converted: 'Converted to Order',
  rejected: 'Rejected',
};

export const SERVICE_REQUEST_STATUS_VARIANT: Record<ServiceRequestStatus, BadgeVariant> = {
  pending: 'warning',
  approved: 'info',
  converted: 'success',
  rejected: 'danger',
};
